/**
 * TrafficChart
 *
 * Recharts area chart showing VPN tunnel throughput over time.
 * Supports per-site drill-down with time range selection.
 */

import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Activity, Loader2 } from 'lucide-react';
import { useTrafficOverview, useSiteTraffic } from '../../hooks/useSecurity';
import { TrafficTimeRangeSelector } from './TrafficTimeRangeSelector';
import { useSecurityStore } from '../../store/securityStore';

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
}

function formatTime(timestamp: string): string {
  const date = new Date(timestamp);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export function TrafficChart() {
  const { selectedSiteId, trafficTimeRange, setTrafficTimeRange } = useSecurityStore();
  const { data: overview, isLoading: overviewLoading } = useTrafficOverview();
  const { data: siteData, isLoading: siteLoading } = useSiteTraffic(
    selectedSiteId || '',
    trafficTimeRange
  );

  const isLoading = selectedSiteId ? siteLoading : overviewLoading;

  return (
    <div className="bg-ems-bg-card rounded-2xl border border-ems-border p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-ems-text-muted" />
          <h2 className="text-sm font-semibold text-ems-text-primary uppercase tracking-wider">
            {selectedSiteId ? 'Site Traffic' : 'Fleet Traffic Overview'}
          </h2>
        </div>
        {selectedSiteId && (
          <TrafficTimeRangeSelector
            value={trafficTimeRange}
            onChange={setTrafficTimeRange}
          />
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-5 h-5 animate-spin text-ems-text-muted" />
        </div>
      ) : selectedSiteId && siteData?.metrics ? (
        /* Per-site time-series chart */
        siteData.metrics.length === 0 ? (
          <div className="py-12 text-center text-sm text-ems-text-muted">
            No traffic data for the selected time range
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={siteData.metrics}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(52, 211, 153, 0.06)" />
              <XAxis
                dataKey="time"
                tickFormatter={formatTime}
                tick={{ fontSize: 11 }}
                stroke="#64748b"
              />
              <YAxis
                tickFormatter={(v) => formatBytes(v)}
                tick={{ fontSize: 11 }}
                stroke="#64748b"
                width={70}
              />
              <Tooltip
                formatter={(value: number, name: string) => [formatBytes(value), name]}
                labelFormatter={(label) => new Date(label).toLocaleString()}
                contentStyle={{ backgroundColor: '#111d2e', border: '1px solid rgba(52, 211, 153, 0.12)', borderRadius: 8, color: '#e2e8f0' }}
              />
              <Area
                type="monotone"
                dataKey="ingress_bytes"
                name="Ingress"
                stroke="#38bdf8"
                fill="#38bdf8"
                fillOpacity={0.1}
              />
              <Area
                type="monotone"
                dataKey="egress_bytes"
                name="Egress"
                stroke="#34d399"
                fill="#34d399"
                fillOpacity={0.1}
              />
            </AreaChart>
          </ResponsiveContainer>
        )
      ) : (
        /* Fleet overview table */
        <div className="overflow-hidden">
          {!overview?.traffic?.length ? (
            <div className="py-12 text-center text-sm text-ems-text-muted">
              No traffic data available
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ems-border">
                  <th className="text-left px-3 py-2 text-xs font-semibold text-ems-text-muted uppercase">Site</th>
                  <th className="text-right px-3 py-2 text-xs font-semibold text-ems-text-muted uppercase">Ingress</th>
                  <th className="text-right px-3 py-2 text-xs font-semibold text-ems-text-muted uppercase">Egress</th>
                  <th className="text-right px-3 py-2 text-xs font-semibold text-ems-text-muted uppercase">Bandwidth</th>
                </tr>
              </thead>
              <tbody>
                {overview.traffic.map((t) => (
                  <tr key={t.site_id} className="border-b border-ems-border hover:bg-ems-bg-card-hover">
                    <td className="px-3 py-2 text-ems-text-primary">{t.site_name}</td>
                    <td className="px-3 py-2 text-right text-ems-text-secondary font-mono text-xs">
                      {formatBytes(t.ingress_bytes)}
                    </td>
                    <td className="px-3 py-2 text-right text-ems-text-secondary font-mono text-xs">
                      {formatBytes(t.egress_bytes)}
                    </td>
                    <td className="px-3 py-2 text-right text-ems-text-secondary font-mono text-xs">
                      {formatBytes(t.bandwidth_bps)}/s
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
